import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useCollaboration } from '../contexts/CollaborationContext';
import { UserCursor, CollaborationUser } from '../types/Collaboration';

/**
 * Throttle interval for cursor updates (50ms for smooth updates)
 */
const CURSOR_THROTTLE_MS = 50;

/**
 * Timeout for considering a cursor as stale (hide if no update in 5 seconds)
 */
const CURSOR_STALE_TIMEOUT_MS = 5000;

/**
 * Options for useCursorSync hook
 */
interface UseCursorSyncOptions {
  /**
   * Whether cursor sync is enabled
   */
  enabled?: boolean;

  /**
   * Canvas offset (stagePos)
   */
  stagePos: { x: number; y: number };

  /**
   * Canvas scale
   */
  stageScale: number;

  /**
   * Canvas container ref for coordinate conversion
   */
  canvasContainerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Cursor info for rendering
 */
export interface RemoteCursor {
  userId: string;
  userName: string;
  color: string;
  cursor: UserCursor;
  lastUpdate: number;
  isStale: boolean;
}

/**
 * Return type of useCursorSync hook
 */
interface UseCursorSyncReturn {
  /**
   * Remote cursors to render (other users)
   */
  remoteCursors: RemoteCursor[];

  /**
   * Handler to call on mouse move (throttled internally)
   */
  handleMouseMove: (e: React.MouseEvent) => void;

  /**
   * Handler to call when mouse leaves canvas
   */
  handleMouseLeave: () => void;
}

/**
 * Hook for synchronizing cursor positions in collaborative mode
 *
 * Features:
 * - Sends local cursor position with throttle (50ms)
 * - Receives and tracks other users' cursor positions
 * - Converts between screen and canvas coordinates
 * - Marks stale cursors (no update in 5 seconds)
 */
export function useCursorSync(options: UseCursorSyncOptions): UseCursorSyncReturn {
  const { enabled = true, stagePos, stageScale, canvasContainerRef } = options;

  const { state, sendCursor } = useCollaboration();
  const { mode, currentUser, users } = state;

  // Throttle state
  const lastSendTimeRef = useRef<number>(0);
  const pendingCursorRef = useRef<UserCursor | null>(null);
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Convert screen coordinates to canvas coordinates
   */
  const screenToCanvas = useCallback(
    (screenX: number, screenY: number): UserCursor | null => {
      if (!canvasContainerRef.current) return null;

      const rect = canvasContainerRef.current.getBoundingClientRect();
      const canvasX = (screenX - rect.left - stagePos.x) / stageScale;
      const canvasY = (screenY - rect.top - stagePos.y) / stageScale;

      return { x: canvasX, y: canvasY };
    },
    [stagePos.x, stagePos.y, stageScale, canvasContainerRef]
  );

  /**
   * Send cursor with throttle
   */
  const sendCursorThrottled = useCallback(
    (cursor: UserCursor | null) => {
      // If not in collaborative mode or disabled, skip
      if (mode !== 'collaborative' || !enabled) return;

      const now = Date.now();
      const timeSinceLastSend = now - lastSendTimeRef.current;

      if (timeSinceLastSend >= CURSOR_THROTTLE_MS) {
        // Send immediately
        sendCursor(cursor);
        lastSendTimeRef.current = now;
        pendingCursorRef.current = null;

        // Clear any pending timeout
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
          throttleTimeoutRef.current = null;
        }
      } else {
        // Store pending cursor and schedule send
        pendingCursorRef.current = cursor;

        if (!throttleTimeoutRef.current) {
          throttleTimeoutRef.current = setTimeout(() => {
            if (pendingCursorRef.current !== null) {
              sendCursor(pendingCursorRef.current);
              lastSendTimeRef.current = Date.now();
              pendingCursorRef.current = null;
            }
            throttleTimeoutRef.current = null;
          }, CURSOR_THROTTLE_MS - timeSinceLastSend);
        }
      }
    },
    [mode, enabled, sendCursor]
  );

  /**
   * Handle mouse move on canvas
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const cursor = screenToCanvas(e.clientX, e.clientY);
      if (cursor) {
        sendCursorThrottled(cursor);
      }
    },
    [screenToCanvas, sendCursorThrottled]
  );

  /**
   * Handle mouse leave canvas
   */
  const handleMouseLeave = useCallback(() => {
    sendCursorThrottled(null);
  }, [sendCursorThrottled]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Send null cursor when leaving collaborative mode
   */
  useEffect(() => {
    if (mode !== 'collaborative') {
      // Clear pending cursors when leaving collaborative mode
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }
      pendingCursorRef.current = null;
    }
  }, [mode]);

  /**
   * Build remote cursors from users (excluding current user)
   */
  const remoteCursors = useMemo((): RemoteCursor[] => {
    if (mode !== 'collaborative' || !enabled || !currentUser) {
      return [];
    }

    const now = Date.now();

    return users
      .filter((user: CollaborationUser) => {
        // Exclude current user
        if (user.id === currentUser.id) return false;
        // Must have a valid cursor position
        if (!user.presence?.cursor) return false;
        return true;
      })
      .map((user: CollaborationUser) => {
        const lastUpdate = user.presence?.lastUpdate ?? 0;
        const isStale = now - lastUpdate > CURSOR_STALE_TIMEOUT_MS;

        return {
          userId: user.id,
          userName: user.name,
          color: user.color,
          cursor: user.presence!.cursor!,
          lastUpdate,
          isStale,
        };
      })
      .filter((cursor: RemoteCursor) => !cursor.isStale); // Hide stale cursors
  }, [mode, enabled, currentUser, users]);

  return {
    remoteCursors,
    handleMouseMove,
    handleMouseLeave,
  };
}

export default useCursorSync;
